/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */

import { useTranslations } from '@/hooks/useTranslations';
import { FlexBox, Text, Label, RadioButton, Ui5CustomEvent, RadioButtonDomRef } from '@ui5/webcomponents-react';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import DropdownComponent from '../DropdownComponent';
import { SelectedState, actions, getBusinessType, storageUtils, extractIdAndBusinessType } from './BestPracticeTemplateFor42kUtils';
import TaskTitle from '../TaskTitle/TaskTitle';
import { fetchSystemSelectionData } from '../JobTable/JobTableUtils';
import { useAppSelector } from '@/hooks';
import TaskIcon from '@/components/TaskIcon';

const BestPracticeTemplateFor42k: React.FC<any> = ({ content }) => {
    const dispatch = useDispatch();
    const productList = useAppSelector(state => state.productList);
    const [s4SystemList, setS4SystemList] = useState<{ display: string; value: string }[]>([]);
    const [bNSystemList, setBNSystemList] = useState<{ display: string; value: string }[]>([]);
    const [systemOptions, setSystemOptions] = useState<{ display: string; value: string }[]>([]);
    const [anidOptions, setAnidOptions] = useState<{ display: string; value: string }[]>([]);

    const { t } = useTranslations();
    const PROD = 'Prod';
    let selectedOption = 'Non Prod';

    let personalizationValue = content.personalizationConfiguration?.configurationValue;
    if (!personalizationValue) {
        personalizationValue = {
            buyerAccountSBN: { id: '', businessTypeId: '' },
            s4HanaPC: { id: '', businessTypeId: '' },
        };
    } else {
        personalizationValue = JSON.parse(personalizationValue);
        personalizationValue.buyerAccountSBN.businessTypeId = getBusinessType(personalizationValue.buyerAccountSBN.businessTypeId);
        personalizationValue.s4HanaPC.businessTypeId = getBusinessType(personalizationValue.s4HanaPC.businessTypeId);
    }

    const [selected, setSelected] = useState<SelectedState>(personalizationValue);
    const description = [t('SYSTEM_INFORMATION_42K_S4'), t('SYSTEM_INFORMATION_42K_ANID')];

    useEffect(() => {
        fetchSystemSelectionData(productList, 0).then(data => {
            if (data) {
                handleSystemSelectionData(data);
            }
        });
    }, []);

    const handleSystemSelectionData = (data: any) => {
        const createOption = (id: string, businessTypeId: string) => ({
            display: `${id} ${getBusinessType(businessTypeId)}`,
            value: `${id} ${businessTypeId}`,
        });

        const s4pcList = (data?.s4pcList || []).map((item: any) => createOption(item.cldTenantShortId, item.cldTenantBusinessType));
        const anidList = (data?.anidList || []).map((item: any) => createOption(item.cldTenantExternalId, item.cldTenantBusinessType));

        setS4SystemList(s4pcList);
        setBNSystemList(anidList);
        setSystemOptions(
            selectedOption === PROD
                ? s4pcList.filter((opt: { display: string | string[] }) => opt.display.includes(PROD))
                : s4pcList.filter((opt: { display: string | string[] }) => !opt.display.includes(PROD))
        );
        setAnidOptions(
            selectedOption === PROD
                ? anidList.filter((opt: { display: string | string[] }) => opt.display.includes(PROD))
                : anidList.filter((opt: { display: string | string[] }) => !opt.display.includes(PROD))
        );
    };

    const handleChange = (event: any, key: keyof SelectedState) => {
        const selectedOptionValue = event.detail.selectedOption.dataset.value;
        const { id, businessTypeId } = extractIdAndBusinessType(selectedOptionValue);
        console.log(`Updating ${key}: id=${id}, businessTypeId=${businessTypeId}`);
        setSelected(prevState => ({ ...prevState, [key]: { id, businessTypeId } }));
    };

    const handleEnvChange = (event: Ui5CustomEvent<RadioButtonDomRef, never>) => {
        setSystemOptions(
            event?.target?.text === PROD
                ? s4SystemList.filter(opt => opt.display.includes(PROD))
                : s4SystemList.filter(opt => !opt.display.includes(PROD))
        );
        setAnidOptions(
            event?.target?.text === PROD
                ? bNSystemList.filter(opt => opt.display.includes(PROD))
                : bNSystemList.filter(opt => !opt.display.includes(PROD))
        );
    };

    useEffect(() => {
        updateStateVariable();
    }, [selected]);

    const handleChecked = (env: string) => {
        if (personalizationValue === undefined && env !== PROD) {
            selectedOption = env;
            return true;
        } else if (
            env === PROD &&
            personalizationValue.buyerAccountSBN.businessTypeId.includes(PROD) &&
            personalizationValue.s4HanaPC.businessTypeId.includes(PROD)
        ) {
            selectedOption = env;
            return true;
        } else if (
            env !== PROD &&
            !personalizationValue.buyerAccountSBN.businessTypeId.includes(PROD) &&
            !personalizationValue.s4HanaPC.businessTypeId.includes(PROD)
        ) {
            selectedOption = env;
            return true;
        }
        return false;
    };

    const updateStateVariable = () => {
        const USER_ID = storageUtils.getItem('user').user_uuid;
        dispatch(
            actions.setCurrentProductConfigurationValue({
                productRunId: productList.currentRunId,
                taskId: content.id,
                configurationKey: content.productConfigurationKey,
                configurationValue: JSON.stringify(selected),
                createdBy: USER_ID,
                updatedBy: USER_ID,
                personalizationConfiguration: content.personalizationConfiguration,
            })
        );
        dispatch(actions.setCurrentProductConfigKeys({ key: content.productConfigurationKey, value: JSON.stringify(selected) }));
    };

    return (
        <div className="content__datacontroller">
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', width: '100%' }}>
                <TaskIcon icon="it-system" />
                <TaskTitle />
                <Text data-testid="title-integrate-systems">{t('SELECT_SYSTEMS_INTEGRATE')}</Text>
                <FlexBox alignItems="Center" justifyContent="Center" role="radiogroup">
                    <Label data-testid="label-description-env" showColon>
                        {t('ENV_LABEL')}{' '}
                    </Label>
                    <RadioButton
                        name="ENV"
                        data-testid="radio-button-non-prod"
                        text={t('NON_PROD_SYSTEM')}
                        value="Non Prod"
                        onChange={handleEnvChange}
                        checked={handleChecked('Non Prod')}
                    />
                    <RadioButton
                        name="ENV"
                        data-testid="radio-button-prod"
                        text={t('PROD_SYSTEM')}
                        value="Prod"
                        onChange={handleEnvChange}
                        checked={handleChecked('Prod')}
                    />
                </FlexBox>
                <div style={{ padding: '1rem' }}>
                    {(Object.keys(selected) as Array<keyof SelectedState>).map((key, index) => (
                        <FlexBox key={key} direction="Column">
                            <Text style={{ marginBottom: '0.5rem' }} data-testid={`title-description-${index}`}>
                                {description[index]}
                            </Text>
                            <DropdownComponent
                                label=""
                                dropdownKey={key}
                                selectedValue={`${selected[key].id} ${selected[key].businessTypeId}`}
                                options={(key === 'buyerAccountSBN' ? systemOptions : anidOptions).map(opt => opt.display)}
                                handlechange={e => handleChange(e, key)}
                                data-testid={`dropdown-${key}`}
                                fullWidth
                            />
                        </FlexBox>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default BestPracticeTemplateFor42k;
